import { getMessageWithPlaceholders } from 'shared-locales';
import { useAchievementTypes, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Alert, ConfirmButton } from 'components/common';

import type { Achievement, AchievementData } from 'shared-types';

interface Props {
  achievement: Pick<
    Achievement,
    | 'allowClosingConversation'
    | 'allowClosingConversationUser'
    | 'allowClosingConversationRequireApproval'
    | 'allowConfidential'
    | 'type'
  > & { isOwnerOrSuperadmin?: boolean };
  achievementData: Pick<
    AchievementData,
    'isClosed' | 'status' | 'isConfidential' | 'confidentialUsers'
  >;
  achievementId: number;
  completionIdentifier: number;
  isUserOrMember: boolean;
  updateData: () => Promise<void>;
}

/**
 * Displays a button to close achievement completion conversations with support for different closing scenarios.
 *
 * Handles two main closing flows:
 * 1. Standard closing: Directly closes the conversation
 * 2. Confidential with approval: Requires approval from all confidential users before closing
 *
 * The component manages permissions based on:
 * - User role (superadmin/owner vs regular user)
 * - Achievement settings (allowClosingConversationUser, allowClosingConversationRequireApproval)
 * - Confidential conversation approval status
 *
 * @param achievement - Achievement settings including closing permissions and confidentiality settings
 * @param achievementData - Current completion data including closure and confidential status
 * @param achievementId - ID of the achievement for API calls
 * @param completionIdentifier - Identifier for the completion (userId for user completions, achievementGroupCompletionId for group completions)
 * @param isUserOrMember - Whether the current user is the completion target or a group member
 * @param updateData - Callback to refresh completion data after closing/approving
 */
const AchievementCompletionsCloseConversation: React.FC<Props> = ({
  achievement,
  achievementData,
  achievementId,
  completionIdentifier,
  isUserOrMember,
  updateData
}) => {
  const { dispatch } = useDispatch();

  const { messages, user } = useReduxState();

  const { isGroupAchievement } = useAchievementTypes({
    type: achievement?.type
  });

  const isConfidentialWithApproval =
    achievementData.isConfidential &&
    achievement.allowClosingConversationRequireApproval;

  const canClickButton = (() => {
    // Superadmins and owners can always close the conversation
    if (achievement.isOwnerOrSuperadmin) return true;

    // Check if user is allowed to close the conversation
    if (isUserOrMember && !achievement.allowClosingConversationUser) {
      return false;
    }

    // Check if user has already given approval for confidential conversations
    if (isConfidentialWithApproval && achievementData.confidentialUsers) {
      const userApproval = achievementData.confidentialUsers.find(
        (confUser) => confUser.UserId === user?.id
      );
      if (userApproval?.closingApprovalDate) return false;
    }

    return true;
  })();

  const handleConfirm = async () => {
    try {
      const payload = {
        updatedCompletion: { isClosed: true },
        ...(!isGroupAchievement ? { userId: completionIdentifier } : {})
      };

      const url = isGroupAchievement
        ? `/admin/achievements/${achievementId}/group-completions/${completionIdentifier}`
        : `/admin/achievements/${achievementId}/completions`;

      const response = await api.put(url, payload);
      await updateData();

      // Determine appropriate success message based on what actually happened
      let successMessage: string;
      if (isConfidentialWithApproval) {
        const conversationWasClosed = response.data?.user?.isClosed === true;

        successMessage = conversationWasClosed
          ? messages.achievementCompletionCloseConversationSuccess
          : messages.achievementCompletionCloseConversationApprovalRecorded;
      } else {
        successMessage = messages.achievementCompletionCloseConversationSuccess;
      }

      dispatch.addNotice({ text: successMessage, type: 'success' });

      return true;
    } catch (err) {
      return false;
    }
  };

  const buttonText = isConfidentialWithApproval
    ? messages.achievementCompletionApproveClosing
    : messages.achievementCompletionCloseConversation;

  const currentUserApproval = achievementData.confidentialUsers?.find(
    (confUser) => confUser.UserId === user?.id
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {!achievementData.isClosed && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            padding: '5px 0'
          }}
        >
          <ConfirmButton
            actionText={buttonText}
            actionCategory="danger"
            category="light"
            confirmText={
              <div>
                <Alert
                  style={{ marginBottom: 20 }}
                  text={messages.achievementCompletionCloseConversationWarning}
                  type="warning-light"
                />

                {isConfidentialWithApproval ? (
                  <div>
                    {(() => {
                      const approvedCount =
                        achievementData.confidentialUsers?.filter(
                          (cu) => cu.closingApprovalDate
                        ).length || 0;
                      const totalRequired =
                        achievementData.confidentialUsers?.length || 0;
                      const needsMoreApprovals =
                        approvedCount < totalRequired - 1;

                      if (needsMoreApprovals) {
                        return getMessageWithPlaceholders({
                          message:
                            messages.achievementCompletionCloseConversationApprovalNeeded,
                          placeholders: {
                            totalCount: totalRequired.toString(),
                            approvedCount: approvedCount.toString()
                          }
                        });
                      } else {
                        return messages.achievementCompletionCloseConversationApprovalWillCloseNow;
                      }
                    })()}
                  </div>
                ) : (
                  <div>
                    {messages.achievementCompletionCloseConversationConfirm}
                  </div>
                )}
              </div>
            }
            confirmTitle={buttonText}
            buttonIcon={isConfidentialWithApproval ? 'check-circle' : 'ban'}
            buttonSize="header"
            buttonText={buttonText}
            buttonTooltip={
              currentUserApproval?.closingApprovalDate
                ? messages.achievementCompletionCloseConversationApprovedByTooltip
                : undefined
            }
            disabled={!canClickButton}
            modalWidth="medium"
            onConfirm={handleConfirm}
          />
        </div>
      )}
    </div>
  );
};

export default AchievementCompletionsCloseConversation;
